'use client';

import Link from 'next/link';

import {
  BadgeDollarSign,
  Calculator,
  ChevronDown,
  ClipboardList,
  Clock,
  FileText,
  Menu,
  Package,
  Percent,
  Ship,
  ShoppingCart,
  TrendingDown,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { NavigationMenu, NavigationMenuList } from '@kit/ui/navigation-menu';

import { SiteNavigationItem } from './site-navigation-item';

const links = [
  { label: 'How It Works', path: '/#how' },
  { label: 'Features', path: '/#features' },
  // The footer, the sitemap and every tool page point at /pricing, so the
  // header has to land in the same place — two "Pricing" links going to two
  // different pages read as two different offers.
  { label: 'Pricing', path: '/pricing' },
  { label: 'FAQ', path: '/faq' },
];

// Homepage section links in the Resources menu.
const sectionLinks = [
  { label: 'Built For', path: '/#benefits' },
  { label: 'Founder Story', path: '/#founder' },
  { label: 'Free Tools', path: '/#tools' },
  { label: 'Partners & Offers', path: '/partners' },
  { label: 'Agency Partners', path: '/agency-partners' },
];

const toolLinks = [
  {
    label: 'Purchase Order Creator',
    path: '/free-purchase-order-creator',
    icon: ClipboardList,
    description: 'Build a PO and download the PDF',
  },
  {
    label: 'Amazon Profit Calculator',
    path: '/tools/amazon-profit-calculator',
    icon: BadgeDollarSign,
    description: 'FBA profit, margin & break-even ACoS',
  },
  {
    label: 'Dropshipping Profit Calculator',
    path: '/tools/dropshipping-profit-calculator',
    icon: ShoppingCart,
    description: 'Ecommerce profit, margin & break-even ROAS',
  },
  {
    label: 'Profit Margin Calculator',
    path: '/tools/profit-margin-calculator',
    icon: Percent,
    description: 'Gross margin, net margin & markup',
  },
  {
    label: 'Import Duty Calculator',
    path: '/tools/import-duty-calculator',
    icon: Calculator,
    description: 'Estimate customs duties & tariffs',
  },
  {
    label: 'Landed Cost Calculator',
    path: '/tools/landed-cost-calculator',
    icon: TrendingDown,
    description: 'Total cost including all fees',
  },
  {
    label: 'Freight Cost Estimator',
    path: '/tools/freight-cost-estimator',
    icon: Ship,
    description: 'Compare sea vs air freight costs',
  },
  {
    label: 'MOQ Calculator',
    path: '/tools/moq-calculator',
    icon: Package,
    description: 'Find your optimal order quantity',
  },
  {
    label: 'Lead Time Calculator',
    path: '/tools/lead-time-calculator',
    icon: Clock,
    description: 'Calculate reorder points & safety stock',
  },
  {
    label: 'Free Templates',
    path: '/tools/templates',
    icon: FileText,
    description: 'Excel templates for importers',
  },
];

export function SiteNavigation() {
  const NavItems = links.map((item) => (
    <SiteNavigationItem key={item.path} path={item.path}>
      {item.label}
    </SiteNavigationItem>
  ));

  return (
    <>
      <div className={'hidden items-center justify-center lg:flex'}>
        <NavigationMenu>
          <NavigationMenuList className={'gap-x-2.5'}>
            {NavItems}
            <ToolsDropdown />
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      <div className={'flex justify-start sm:items-center lg:hidden'}>
        <MobileDropdown />
      </div>
    </>
  );
}

function ToolsDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground inline-flex w-max items-center gap-1 text-sm font-semibold transition-colors">
        Resources
        <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72 p-2">
        {sectionLinks.map((section) => (
          <DropdownMenuItem key={section.path} asChild>
            <Link href={section.path}>{section.label}</Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {toolLinks.map((tool) => {
          const Icon = tool.icon;
          return (
            <DropdownMenuItem key={tool.path} asChild>
              <Link
                href={tool.path}
                className="flex items-start gap-3 rounded-md px-2 py-2"
              >
                <div className="bg-primary/10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                  <Icon className="text-primary h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-medium">{tool.label}</div>
                  <div className="text-muted-foreground text-xs">
                    {tool.description}
                  </div>
                </div>
              </Link>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            href="/tools"
            className="text-primary flex items-center justify-center gap-1 px-2 py-2 text-sm font-medium"
          >
            View all free tools →
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label={'Open Menu'}>
        <Menu className={'h-8 w-8'} />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={'max-h-[calc(100dvh-5rem)] w-full overflow-y-auto'}
      >
        {links.map((item) => (
          <DropdownMenuItem key={item.path} asChild>
            <Link className="flex h-full w-full items-center" href={item.path}>
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
        {sectionLinks.map((section) => (
          <DropdownMenuItem key={section.path} asChild>
            <Link
              className="flex h-full w-full items-center"
              href={section.path}
            >
              {section.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            className="text-primary flex h-full w-full items-center font-medium"
            href="/tools"
          >
            Browse all free tools
          </Link>
        </DropdownMenuItem>
        {toolLinks.map((tool) => (
          <DropdownMenuItem key={tool.path} asChild>
            <Link
              className="flex h-full w-full items-center pl-4 text-sm"
              href={tool.path}
            >
              {tool.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
